import create from "zustand"
import setObj from "lodash-es/set"
import produce from "immer"
import { mountStoreDevtool } from "simple-zustand-devtools"
import { invert, update } from "lodash-es"
import { NOTIFICATION_KINDS } from "data/static.items"
import replaceTemplateVariables from "utils/replace-template-variables"

export type TriggerStateType = {
  id?: string
  broadcastTime?: Date
  content: {
    body?: string
    title?: string
    extraFields?: Record<string, any>
  }
  dataVariables?: any
  providerVariables?: { key: string; value: string | number }[]
  kind?: any
  provider?: any
  isProgrammatic?: boolean
  filters?: any[]
  template?: any
  userQuery?: any
}
export type NotificationTriggerStoreType = {
  data: TriggerStateType
  setData: (path: string, value: any) => void
  resetData: () => void
  removeListItem: (
    key: string,
    index: number,
    updateLastSaved?: boolean
  ) => void
  addListItem: (key: string, item: any) => void
  initializeState: (state: TriggerStateType) => void
  getFilters: () => any[]
}
const useNotificationTriggerStore = create<NotificationTriggerStoreType>(
  (set, get) => ({
    data: {
      broadcastTime: new Date(),
      content: {},
    },
    setData(path, value) {
      set(
        produce((draft: NotificationTriggerStoreType) => {
          if (path === "kind") {
            draft.data.provider = null
            if (value !== "email" && draft.data.content.body) {
              draft.data.content.body = new DOMParser().parseFromString(
                draft.data.content.body,
                "text/html"
              ).documentElement.textContent
            }
          }
          if (path === "template") {
            draft.data.kind = NOTIFICATION_KINDS.find(
              kind => kind.id === value.provider.kind.toLowerCase()
            )

            draft.data.provider = value.provider
            draft.data.providerVariables = Object.keys(
              value.providerVariables
            )?.map(key => ({ key, value: value.providerVariables[key] }))

            draft.data.content.title = replaceTemplateVariables(
              value.title,
              invert(value?.dataSource?.outputs)
            )
            draft.data.content.body = replaceTemplateVariables(
              value.body,
              invert(value?.dataSource?.outputs)
            )

            draft.data.content.extraFields = {
              ...value.extraFields,
              data: JSON.parse(value.extraFields.data),
            }
          }
          if (path === "userQuery") {
            draft.data.filters = []
          }
          setObj(draft.data, path, value)
        })
      )
    },
    resetData() {
      set(
        produce((draft: NotificationTriggerStoreType) => {
          draft.data = {
            content: {},
            broadcastTime: new Date(),
          }
        })
      )
    },
    addListItem(key: string, item: any) {
      set(
        produce((store: NotificationTriggerStoreType) => {
          update(store.data, key, val => (val?.length ? val : []).concat(item))
        })
      )
    },
    removeListItem(key, index) {
      set(
        produce((store: NotificationTriggerStoreType) => {
          update(store.data, key, val =>
            val.filter((_: any, i: number) => i !== index)
          )
        })
      )
    },
    initializeState(state) {
      set(
        produce((store: NotificationTriggerStoreType) => {
          store.data = state
        })
      )
    },
    getFilters() {
      return get()
        .data.filters?.filter(f => f?.field)
        ?.map(({ field }) => ({
          id: field.id,
          value: field.value?.map(f => {
            if (f?.id) return f?.id
            if (f instanceof Date) return f?.toISOString()
            return f
          }),
          exclude: field.exclude ? true : false,
        }))
    },
  })
)
if (process.env.NODE_ENV === "development") {
  mountStoreDevtool("NotificationTriggerStore", useNotificationTriggerStore)
}
export default useNotificationTriggerStore
